import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Sparkles, Users } from 'lucide-react';
import { heroData, aboutData, speakingData, tieTheMissionData, images } from '../data/mock';

const HomePage = () => {
  return (
    <main>
      {/* Hero Section */}
      <section className="min-h-screen flex items-center relative overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={images.hero}
            alt="Tyrre Burks"
            className="w-full h-full object-cover"
            style={{ objectPosition: '50% 25%' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d0d0d] via-[#0d0d0d]/80 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent"></div>
        </div>

        <div className="container-custom relative z-10 pt-32 pb-20">
          <div className="max-w-3xl">
            <div className="mb-6 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
              <span className="caption text-[#c9a86c]">{heroData.subheadline}</span>
            </div>
            
            <h1 className="heading-1 text-[#f5f5f0] mb-8 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
              {heroData.headline}
            </h1>
            
            <p className="body-large text-[#a8a8a0] mb-10 max-w-xl opacity-0 animate-fade-in-up" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
              {heroData.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in-up" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
              <Link to="/book" className="btn-primary inline-flex items-center justify-center gap-2">
                {heroData.ctaPrimary}
                <ArrowRight size={18} />
              </Link>
              <Link to="/ty-the-mission" className="btn-secondary inline-flex items-center justify-center gap-2">
                {heroData.ctaSecondary}
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-0 animate-fade-in" style={{ animationDelay: '1s', animationFillMode: 'forwards' }}>
          <span className="text-[#6b6b65] text-xs tracking-widest uppercase">Scroll</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#c9a86c] to-transparent"></div>
        </div>
      </section>

      {/* Framework Overview */}
      <section className="section-padding bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-[#c9a86c] mb-4 block">The Framework</span>
            <h2 className="heading-2 text-[#f5f5f0] mb-6">{tieTheMissionData.headline}</h2>
            <div className="gold-accent mx-auto mb-8"></div>
            <p className="body-large text-[#a8a8a0] max-w-2xl mx-auto">
              {tieTheMissionData.tagline}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Users, data: tieTheMissionData.framework.roles },
              { icon: Target, data: tieTheMissionData.framework.needs },
              { icon: Sparkles, data: tieTheMissionData.framework.gifts },
            ].map((item, index) => (
              <div
                key={item.data.title}
                className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8 card-hover group"
              >
                <div className="w-12 h-12 flex items-center justify-center border border-[#c9a86c]/30 text-[#c9a86c] mb-6 group-hover:bg-[#c9a86c] group-hover:text-[#0d0d0d] transition-all">
                  <item.icon size={24} />
                </div>
                <h3 className="heading-3 text-[#f5f5f0] mb-3">{item.data.title}</h3>
                <p className="body-medium text-[#a8a8a0] mb-4">{item.data.description}</p>
                <p className="text-sm text-[#6b6b65] italic">"{item.data.question}"</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/ty-the-mission" className="btn-secondary inline-flex items-center gap-2">
              Explore the Framework
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* About Preview */}
      <section className="section-padding bg-[#1a1a18]">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={images.about}
                  alt="Tyrre Burks"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-32 h-32 border border-[#c9a86c]/30 hidden lg:block"></div>
            </div>

            <div>
              <span className="caption text-[#c9a86c] mb-4 block">About Tyrre</span>
              <h2 className="heading-2 text-[#f5f5f0] mb-6">{aboutData.headline}</h2>
              <div className="gold-accent mb-8"></div>
              <p className="body-large text-[#a8a8a0] mb-8 whitespace-pre-line">
                {aboutData.bio.split('\n\n')[0]}
              </p>
              
              <blockquote className="border-l-2 border-[#c9a86c] pl-6 mb-8">
                <p className="text-xl font-display italic text-[#f5f5f0]">
                  "{aboutData.philosophy}"
                </p>
              </blockquote>

              <Link to="/about" className="btn-secondary inline-flex items-center gap-2">
                Learn More
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Speaking Preview */}
      <section className="section-padding bg-[#0d0d0d]">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="caption text-[#c9a86c] mb-4 block">Speaking</span>
            <h2 className="heading-2 text-[#f5f5f0] mb-6">{speakingData.headline}</h2>
            <div className="gold-accent mx-auto mb-8"></div>
            <p className="body-large text-[#a8a8a0] max-w-2xl mx-auto">
              {speakingData.intro}
            </p>
          </div>

          {/* Featured Topics */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
            {speakingData.topics.map((topic, index) => (
              <div
                key={topic.title}
                className="bg-[#1a1a18] border border-[rgba(168,168,160,0.1)] p-8 card-hover"
              >
                <span className="caption text-[#c9a86c] mb-4 block">{topic.duration}</span>
                <h3 className="heading-3 text-[#f5f5f0] mb-4">{topic.title}</h3>
                <p className="body-medium text-[#a8a8a0] mb-6">{topic.description}</p>
                <p className="text-sm text-[#6b6b65]">Ideal for: {topic.ideal}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/speaking" className="btn-primary inline-flex items-center gap-2">
              View Speaking Topics
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 bg-[#1a1a18] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-96 h-96 border border-[#c9a86c] rounded-full -translate-y-1/2 translate-x-1/2"></div>
          <div className="absolute bottom-0 left-0 w-64 h-64 border border-[#c9a86c] rounded-full translate-y-1/2 -translate-x-1/2"></div>
        </div>
        
        <div className="container-custom relative z-10 text-center">
          <span className="caption text-[#c9a86c] mb-4 block">Ready to Align?</span>
          <h2 className="heading-2 text-[#f5f5f0] mb-6 max-w-3xl mx-auto">
            Bring Clarity and Integration to Your Next Event
          </h2>
          <p className="body-large text-[#a8a8a0] mb-10 max-w-xl mx-auto">
            Let's start a conversation about how Tyrre can serve your audience.
          </p>
          <Link to="/book" className="btn-primary inline-flex items-center gap-2">
            Book Tyrre to Speak
            <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default HomePage;